import { AlertTriangle, ArrowLeftRight, Bell, Building2, Check, Globe, ListChecks, Lock, Mail, Phone, ShieldCheck, Upload, User } from "lucide-react";
import { useMutation } from "convex/react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { JurisdictionSelect } from "../components/JurisdictionSelect";
import { Avatar, Button, Card } from "../components/ui";
import { convexApi } from "../convexApi";
import { cooperationRequestTypes } from "../lib/cooperation";
import { canViewAffiliationGroup, getUserPracticeAreas, memberCategoryForUser, memberCategoryLabel, technicalRoleLabel } from "../lib/domain";
import { defaultEmailPushSubscriptions, emailPushSubjects } from "../lib/emailPush";
import type { CooperationRequestType, EmailPushSubject, LexData } from "../lib/types";

type ProfilePersistence = {
  generateProfilePhotoUploadUrl: () => Promise<string>;
  updateMyProfile: (input: { name: string; firstName: string; lastName: string; notificationPreference: string; emailPushSubscriptions: EmailPushSubject[]; avatarStorageId?: string | null; mobile: string | null; telegram: string | null; whatsapp: string | null; linkedin: string | null; github: string | null; website: string | null; practiceAreaIds: string[] }) => Promise<unknown>;
  updateMyFirmProfile: (input: {
    name: string;
    jurisdiction: string;
    address: string | null;
    websiteUrl: string | null;
    telephone: string | null;
    contactEmail: string | null;
    linkedin: string | null;
    logoUrl: string | null;
    logoStorageId?: string | null;
    acceptsCooperationRequests: boolean;
    cooperationInterests: CooperationRequestType[];
    languages: string[];
    preferredContactMode: string | null;
    responseTimeIndicator: string | null;
    conflictCheckInstructions: string | null;
    independenceStatement: string;
  }) => Promise<unknown>;
  createMyFirmProfile: (input: { name: string; jurisdiction: string; lawyerCount: number; independenceStatement: string; websiteUrl: string | null }) => Promise<unknown>;
};

export function ProfilePage({ data, mode = "demo" }: { data: LexData; mode?: "convex" | "demo" }) {
  if (mode === "convex") return <LiveProfilePage data={data} />;
  return <ProfilePageContent data={data} />;
}

function LiveProfilePage({ data }: { data: LexData }) {
  const generateProfilePhotoUploadUrl = useMutation(convexApi.members.generateProfilePhotoUploadUrl);
  const updateMyProfile = useMutation(convexApi.members.updateMyProfile);
  const updateMyFirmProfile = useMutation(convexApi.members.updateMyFirmProfile);
  const createMyFirmProfile = useMutation(convexApi.members.createMyFirmProfile);
  const persistence: ProfilePersistence = {
    generateProfilePhotoUploadUrl,
    updateMyProfile: (input) => updateMyProfile({ ...input, avatarStorageId: input.avatarStorageId as never, practiceAreaIds: input.practiceAreaIds as never, notificationPreference: input.notificationPreference as never, emailPushSubscriptions: input.emailPushSubscriptions as never }),
    updateMyFirmProfile: (input) => updateMyFirmProfile(input as never),
    createMyFirmProfile: (input) => createMyFirmProfile(input as never),
  };
  return <ProfilePageContent data={data} persistence={persistence} />;
}

type SectionStatus = "ready" | number;
type NavItem = { id: string; label: string; icon: ReactNode; status?: SectionStatus };

function ProfilePageContent({ data, persistence }: { data: LexData; persistence?: ProfilePersistence }) {
  const firm = data.viewerFirm;
  const initialUserAreas = useMemo(() => getUserPracticeAreas(data.viewer.id, data.userPracticeAreas, data.practiceAreas), [data.viewer.id, data.userPracticeAreas, data.practiceAreas]);
  const affiliation = firm ? data.affiliationGroups.find((group) => group.id === firm.affiliationGroupId) : null;
  const activeAreas = useMemo(() => data.practiceAreas.filter((area) => area.active), [data.practiceAreas]);

  const makeInitialProfile = () => ({
    name: data.viewer.name,
    firstName: data.viewer.firstName ?? data.viewer.name.split(" ")[0] ?? "",
    lastName: data.viewer.lastName ?? data.viewer.name.split(" ").slice(1).join(" ") ?? "",
    email: data.viewer.email,
    representativeType: data.viewer.representativeType,
    avatarUrl: data.viewer.avatarUrl ?? "",
    notificationPreference: data.viewer.notificationPreference ?? "immediate",
    mobile: data.viewer.mobile ?? "",
    telegram: data.viewer.telegram ?? "",
    whatsapp: data.viewer.whatsapp ?? "",
    linkedin: data.viewer.linkedin ?? "",
    github: data.viewer.github ?? "",
    website: data.viewer.website ?? "",
    firmName: firm?.name ?? "",
    firmLogoUrl: firm?.logoUrl ?? "",
    jurisdiction: firm?.jurisdiction ?? "",
    address: firm?.address ?? "",
    websiteUrl: firm?.websiteUrl ?? "",
    telephone: firm?.telephone ?? "",
    contactEmail: firm?.contactEmail ?? "",
    firmLinkedin: firm?.linkedin ?? "",
    acceptsCooperationRequests: firm?.acceptsCooperationRequests === false ? "no" : "yes",
    languagesText: (firm?.languages ?? []).join(", "),
    preferredContactMode: firm?.preferredContactMode ?? "",
    responseTimeIndicator: firm?.responseTimeIndicator ?? "",
    professionalResponsibilityInstructions: firm?.conflictCheckInstructions ?? "",
    independenceStatement: firm?.independenceStatement ?? "",
  });

  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [selectedPracticeAreaIds, setSelectedPracticeAreaIds] = useState(() => initialUserAreas.map((area) => area.id));
  const [selectedCooperationInterests, setSelectedCooperationInterests] = useState<CooperationRequestType[]>(() => firm?.cooperationInterests ?? []);
  const [selectedEmailPushSubscriptions, setSelectedEmailPushSubscriptions] = useState<EmailPushSubject[]>(() => data.viewer.emailPushSubscriptions ?? defaultEmailPushSubscriptions);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);
  const [removePhoto, setRemovePhoto] = useState(false);
  const [firmPhotoFile, setFirmPhotoFile] = useState<File | null>(null);
  const [firmPhotoPreviewUrl, setFirmPhotoPreviewUrl] = useState<string | null>(null);
  const [removeFirmPhoto, setRemoveFirmPhoto] = useState(false);
  const [profile, setProfile] = useState(makeInitialProfile);
  const [active, setActive] = useState("identity");

  const selectedAreas = useMemo(() => new Set(selectedPracticeAreaIds), [selectedPracticeAreaIds]);
  const photoUrl = photoPreviewUrl ?? (removePhoto ? "" : profile.avatarUrl);
  const firmPhotoUrl = firmPhotoPreviewUrl ?? (removeFirmPhoto ? "" : profile.firmLogoUrl);

  const roleCategory = memberCategoryLabel(data.viewer.memberCategory ?? memberCategoryForUser(data.viewer));
  const roleBadges = (data.viewer.roles ?? []).map(technicalRoleLabel);
  const repTypeLabel = profile.representativeType.charAt(0).toUpperCase() + profile.representativeType.slice(1);

  const touch = () => { setSaved(false); setSaveError(null); setDirty(true); };
  const update = (field: keyof typeof profile, value: string) => { touch(); setProfile((current) => ({ ...current, [field]: value })); };

  const toggleCooperationInterest = (type: CooperationRequestType) => { touch(); setSelectedCooperationInterests((current) => (current.includes(type) ? current.filter((item) => item !== type) : [...current, type])); };
  const togglePracticeArea = (areaId: string) => { touch(); setSelectedPracticeAreaIds((current) => (current.includes(areaId) ? current.filter((id) => id !== areaId) : [...current, areaId].slice(0, 10))); };
  const selectAllAreas = () => { touch(); setSelectedPracticeAreaIds(activeAreas.slice(0, 10).map((area) => area.id)); };
  const clearAreas = () => { touch(); setSelectedPracticeAreaIds([]); };
  const toggleEmailPushSubscription = (subject: EmailPushSubject) => { touch(); setSelectedEmailPushSubscriptions((current) => (current.includes(subject) ? current.filter((item) => item !== subject) : [...current, subject])); };

  const readImage = (file: File | null, set: (url: string | null) => void) => {
    if (!file) { set(null); return; }
    const reader = new FileReader();
    reader.onload = () => set(typeof reader.result === "string" ? reader.result : null);
    reader.readAsDataURL(file);
  };
  const onPhotoSelected = (file: File | null) => { touch(); setRemovePhoto(false); setPhotoFile(file); readImage(file, setPhotoPreviewUrl); };
  const onPhotoRemoved = () => { touch(); setPhotoFile(null); setPhotoPreviewUrl(null); setRemovePhoto(true); setProfile((current) => ({ ...current, avatarUrl: "" })); };
  const onFirmPhotoSelected = (file: File | null) => { touch(); setRemoveFirmPhoto(false); setFirmPhotoFile(file); readImage(file, setFirmPhotoPreviewUrl); };
  const onFirmPhotoRemoved = () => { touch(); setFirmPhotoFile(null); setFirmPhotoPreviewUrl(null); setRemoveFirmPhoto(true); setProfile((current) => ({ ...current, firmLogoUrl: "" })); };

  const languages = splitCommaList(profile.languagesText);
  const readinessItems = [
    { label: "personal practice area", complete: selectedPracticeAreaIds.length > 0 },
    { label: "firm jurisdiction and address", complete: Boolean(profile.jurisdiction && profile.address) },
    { label: "firm contact detail", complete: Boolean(profile.contactEmail || profile.telephone || profile.websiteUrl) },
    { label: "expertise exchange availability", complete: profile.acceptsCooperationRequests === "yes" && selectedCooperationInterests.length > 0 },
    { label: "set of languages", complete: languages.length > 0 },
    { label: "professional responsibility note", complete: Boolean(profile.professionalResponsibilityInstructions.trim()) },
    { label: "independence statement", complete: Boolean(profile.independenceStatement.trim()) },
  ];
  const total = readinessItems.length;
  const complete = readinessItems.filter((item) => item.complete).length;
  const missing = total - complete;
  const firstMissing = readinessItems.find((item) => !item.complete)?.label;

  const practiceMissing = selectedPracticeAreaIds.length > 0 ? 0 : 1;
  const firmMissing = [Boolean(profile.jurisdiction && profile.address), Boolean(profile.contactEmail || profile.telephone || profile.websiteUrl), languages.length > 0].filter((ok) => !ok).length;
  const exchangeMissing = profile.acceptsCooperationRequests === "yes" && selectedCooperationInterests.length > 0 ? 0 : 1;
  const standingMissing = [profile.professionalResponsibilityInstructions.trim(), profile.independenceStatement.trim()].filter((ok) => !ok).length;
  const asStatus = (count: number): SectionStatus => (count > 0 ? count : "ready");

  const navItems: NavItem[] = [
    { id: "identity", label: "Identity", icon: <User className="h-4 w-4" /> },
    { id: "contact", label: "Contact & links", icon: <Mail className="h-4 w-4" /> },
    { id: "notifications", label: "Notifications", icon: <Bell className="h-4 w-4" /> },
    { id: "practice", label: "Practice areas", icon: <ListChecks className="h-4 w-4" />, status: asStatus(practiceMissing) },
    ...(firm
      ? [
          { id: "firm", label: "Firm profile", icon: <Building2 className="h-4 w-4" />, status: asStatus(firmMissing) },
          { id: "exchange", label: "Exchange", icon: <ArrowLeftRight className="h-4 w-4" />, status: asStatus(exchangeMissing) },
          { id: "standing", label: "Standing", icon: <ShieldCheck className="h-4 w-4" />, status: asStatus(standingMissing) },
        ]
      : []),
  ];
  const navIds = navItems.map((item) => item.id).join(",");

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const ids = navIds.split(",");
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: [0, 0.2, 0.6, 1] },
    );
    ids.forEach((id) => { const el = document.getElementById(id); if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [navIds]);

  const jump = (id: string) => { document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }); setActive(id); };

  const baseModes: [string, string][] = [["Email", "Email"], ["Phone", "Phone"], ["Video call", "Video call"], ["Messaging app", "Messaging app"], ["Any channel", "Any channel"]];
  const modeOptions: [string, string][] = [["", "No preference"], ...(profile.preferredContactMode && !baseModes.some(([value]) => value === profile.preferredContactMode) ? ([[profile.preferredContactMode, profile.preferredContactMode]] as [string, string][]) : []), ...baseModes];

  const discard = () => {
    setProfile(makeInitialProfile());
    setSelectedPracticeAreaIds(initialUserAreas.map((area) => area.id));
    setSelectedCooperationInterests(firm?.cooperationInterests ?? []);
    setSelectedEmailPushSubscriptions(data.viewer.emailPushSubscriptions ?? defaultEmailPushSubscriptions);
    setPhotoFile(null); setPhotoPreviewUrl(null); setRemovePhoto(false);
    setFirmPhotoFile(null); setFirmPhotoPreviewUrl(null); setRemoveFirmPhoto(false);
    setDirty(false); setSaved(false); setSaveError(null);
  };

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      let avatarStorageId: string | null | undefined;
      if (photoFile && persistence) avatarStorageId = await uploadProfileImage(photoFile, "lawyer photo", persistence);
      else if (removePhoto) avatarStorageId = null;
      let logoStorageId: string | null | undefined;
      if (firmPhotoFile && persistence) logoStorageId = await uploadProfileImage(firmPhotoFile, "firm picture", persistence);
      else if (removeFirmPhoto) logoStorageId = null;
      await persistence?.updateMyProfile({
        name: [profile.firstName, profile.lastName].filter(Boolean).join(" ").trim() || profile.name,
        firstName: profile.firstName,
        lastName: profile.lastName,
        notificationPreference: profile.notificationPreference,
        emailPushSubscriptions: selectedEmailPushSubscriptions,
        avatarStorageId,
        mobile: profile.mobile.trim() || null,
        telegram: profile.telegram.trim() || null,
        whatsapp: profile.whatsapp.trim() || null,
        linkedin: profile.linkedin.trim() || null,
        github: profile.github.trim() || null,
        website: profile.website.trim() || null,
        practiceAreaIds: selectedPracticeAreaIds,
      });
      if (firm) {
        await persistence?.updateMyFirmProfile({
          name: profile.firmName,
          jurisdiction: profile.jurisdiction,
          address: profile.address || null,
          websiteUrl: profile.websiteUrl || null,
          telephone: profile.telephone || null,
          contactEmail: profile.contactEmail || null,
          linkedin: profile.firmLinkedin || null,
          logoUrl: removeFirmPhoto ? null : firmPhotoFile ? null : profile.firmLogoUrl || null,
          logoStorageId,
          acceptsCooperationRequests: profile.acceptsCooperationRequests === "yes",
          cooperationInterests: selectedCooperationInterests,
          languages,
          preferredContactMode: profile.preferredContactMode || null,
          responseTimeIndicator: profile.responseTimeIndicator || null,
          conflictCheckInstructions: profile.professionalResponsibilityInstructions || null,
          independenceStatement: profile.independenceStatement,
        });
      }
      if (photoPreviewUrl) setProfile((current) => ({ ...current, avatarUrl: photoPreviewUrl }));
      if (firmPhotoPreviewUrl) setProfile((current) => ({ ...current, firmLogoUrl: firmPhotoPreviewUrl }));
      setPhotoFile(null); setFirmPhotoFile(null); setRemovePhoto(false); setRemoveFirmPhoto(false);
      setDirty(false);
      setSaved(true);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Profile changes could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="sticky top-0 z-30 mb-6 flex flex-wrap items-center justify-between gap-3 border-b border-line bg-canvas/90 py-3 backdrop-blur">
        <div className="flex items-center gap-3">
          {firm ? <ReadinessRing value={complete} total={total} /> : null}
          <div className="leading-tight">
            <div className="text-sm font-semibold text-ink">Profile and badges</div>
            <div className="mono text-[11px] uppercase tracking-[0.1em] text-muted">{firm ? (missing > 0 ? `Member readiness · ${missing} item${missing > 1 ? "s" : ""} missing` : "Member readiness · all set") : "Your member profile"}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {saved && !dirty ? <span className="mono inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.1em] text-good"><Check className="h-3.5 w-3.5" /> Saved</span> : null}
          <Button type="button" variant="secondary" disabled={!dirty || saving} onClick={discard}>Discard</Button>
          <Button type="button" disabled={!dirty || saving} onClick={() => void save()}>
            <span className="inline-flex items-center gap-2"><Check className="h-4 w-4" />{saving ? "Saving…" : "Save changes"}</span>
          </Button>
        </div>
      </div>

      <header className="mb-6">
        <div className="eyebrow text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Workspace</div>
        <h1 className="mt-1 text-3xl font-semibold text-ink">Profile and badges</h1>
        <p className="mt-2 max-w-2xl text-sm text-secondary">Your member profile, firm details and network badges — everything other members and the secretariat see about {firm?.name ?? profile.name}.</p>
      </header>

      {saveError ? <div className="mb-4 rounded-xl border border-bad/40 bg-bad/5 px-4 py-3 text-sm text-bad">{saveError}</div> : null}
      {!firm ? <FirmCreationCard persistence={persistence} /> : null}

      <div className="grid gap-6 lg:grid-cols-[210px_minmax(0,1fr)]">
        <NavRail items={navItems} active={active} onJump={jump} showCallout={Boolean(firm && firstMissing)} total={total} firstMissing={firstMissing} />

        <div className="grid gap-5">
          <SectionCard id="identity" icon={<User className="h-5 w-5" />} title="Identity" subtitle="Your name and photo as they appear across the network.">
            <PhotoControl name={profile.name} imageUrl={photoUrl} shape="circle" uploadLabel="Upload headshot" fileName={photoFile?.name} hint="JPG or PNG, at least 400×400px. Direct upload only — image URLs aren’t used." onSelect={onPhotoSelected} onRemove={onPhotoRemoved} />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="First name" value={profile.firstName} onChange={(value) => update("firstName", value)} />
              <Field label="Surname" value={profile.lastName} onChange={(value) => update("lastName", value)} />
              <LockedField label="Role" value={roleCategory} badge={roleBadges[0]} />
              <LockedField label="Representative type" value={repTypeLabel} />
            </div>
          </SectionCard>

          <SectionCard id="contact" icon={<Mail className="h-5 w-5" />} title="Contact & links" subtitle="How members reach you directly. Optional channels can stay blank.">
            <div className="grid gap-4 sm:grid-cols-2">
              <LockedField label="Email" value={profile.email} badge="Sign-in" />
              <Field label="Mobile number" value={profile.mobile} onChange={(value) => update("mobile", value)} icon={<Phone className="h-4 w-4" />} placeholder="+352 …" />
              <Field label="Telegram" value={profile.telegram} onChange={(value) => update("telegram", value)} placeholder="@handle or t.me/… link" optional />
              <Field label="WhatsApp" value={profile.whatsapp} onChange={(value) => update("whatsapp", value)} placeholder="number or wa.me/… link" optional />
              <Field label="LinkedIn" value={profile.linkedin} onChange={(value) => update("linkedin", value)} placeholder="linkedin.com/in/…" optional />
              <Field label="GitHub" value={profile.github} onChange={(value) => update("github", value)} placeholder="github.com/…" optional />
              <Field label="Website" value={profile.website} onChange={(value) => update("website", value)} icon={<Globe className="h-4 w-4" />} placeholder="your personal page" optional />
            </div>
          </SectionCard>

          <SectionCard id="notifications" icon={<Bell className="h-5 w-5" />} title="Notifications" subtitle="Choose how often we email you and which network events count.">
            <div className="sm:max-w-sm">
              <SelectField label="Email frequency" value={profile.notificationPreference} onChange={(value) => update("notificationPreference", value)} options={[["immediate", "Immediate"], ["daily digest", "Daily digest"], ["weekly digest", "Weekly digest"], ["critical only", "Critical only"]]} />
            </div>
            <div className="mt-5">
              <div className="mb-2.5 text-sm font-semibold text-ink">Email push subjects</div>
              <EmailPushSubscriptionPicker selected={selectedEmailPushSubscriptions} onToggle={toggleEmailPushSubscription} />
            </div>
          </SectionCard>

          <SectionCard id="practice" icon={<ListChecks className="h-5 w-5" />} title="Your practice areas" subtitle="What you personally advise on. Drives how you surface in member search." status={asStatus(practiceMissing)}>
            <PracticeAreaPicker areas={activeAreas} selected={selectedAreas} count={selectedPracticeAreaIds.length} onToggle={togglePracticeArea} onSelectAll={selectAllAreas} onClear={clearAreas} />
          </SectionCard>

          {firm ? (
            <>
              <SectionCard id="firm" icon={<Building2 className="h-5 w-5" />} title="Firm profile" subtitle="Logo, jurisdiction and contact details shown in the members directory." status={asStatus(firmMissing)}>
                <PhotoControl name={profile.firmName || firm.name} imageUrl={firmPhotoUrl} shape="rounded" uploadLabel="Upload firm logo" fileName={firmPhotoFile?.name} hint="A square logo or firm image works best. Direct upload only — URLs aren’t used." onSelect={onFirmPhotoSelected} onRemove={onFirmPhotoRemoved} />
                <div className="grid gap-4">
                  <Field label="Firm name" value={profile.firmName} onChange={(value) => update("firmName", value)} />
                  <label className="block">
                    <FieldLabel>Jurisdiction</FieldLabel>
                    <JurisdictionSelect value={profile.jurisdiction} onChange={(value) => update("jurisdiction", value)} />
                  </label>
                  <Textarea label="Full firm address" value={profile.address} onChange={(value) => update("address", value)} rows={3} />
                  <div className="grid gap-4 sm:grid-cols-3">
                    <Field label="Website" value={profile.websiteUrl} onChange={(value) => update("websiteUrl", value)} optional />
                    <Field label="Telephone" value={profile.telephone} onChange={(value) => update("telephone", value)} optional />
                    <Field label="Firm email" value={profile.contactEmail} onChange={(value) => update("contactEmail", value)} optional />
                  </div>
                  <Field label="LinkedIn" value={profile.firmLinkedin} onChange={(value) => update("firmLinkedin", value)} placeholder="linkedin.com/company/…" optional />
                  <Field label="Languages" value={profile.languagesText} onChange={(value) => update("languagesText", value)} placeholder="English, French, Luxembourgish" hint="Comma-separated. Used to match cross-border requests." />
                </div>
                {affiliation && canViewAffiliationGroup(data.viewer, firm) ? (
                  <div className="mt-5 rounded-xl border border-line bg-canvas p-3 text-sm">
                    <div className="font-semibold text-ink">{affiliation.name}</div>
                    <p className="mt-1 text-secondary">{affiliation.explanation}</p>
                  </div>
                ) : null}
              </SectionCard>

              <SectionCard id="exchange" icon={<ArrowLeftRight className="h-5 w-5" />} title="Expertise exchange" subtitle="What kinds of member-to-member requests you accept, and how." status={asStatus(exchangeMissing)}>
                <div className="grid gap-4 sm:grid-cols-2">
                  <SelectField label="Accepts exchange requests" value={profile.acceptsCooperationRequests} onChange={(value) => update("acceptsCooperationRequests", value)} options={[["yes", "Yes — open to requests"], ["no", "No — not at this time"]]} />
                  <SelectField label="Preferred request mode" value={profile.preferredContactMode} onChange={(value) => update("preferredContactMode", value)} options={modeOptions} />
                </div>
                <div className="mt-5">
                  <div className="mb-2.5 flex items-center gap-2 text-sm font-semibold text-ink">Interests <span className="mono text-[11px] font-normal uppercase tracking-[0.1em] text-muted">· {selectedCooperationInterests.length} selected</span></div>
                  <CooperationInterestPicker selected={selectedCooperationInterests} onToggle={toggleCooperationInterest} />
                </div>
                <div className="mt-5">
                  <Field label="Response expectation" value={profile.responseTimeIndicator} onChange={(value) => update("responseTimeIndicator", value)} placeholder="e.g. acknowledge within 2 business days" optional />
                </div>
              </SectionCard>

              <SectionCard id="standing" icon={<ShieldCheck className="h-5 w-5" />} title="Professional standing" subtitle="Independence and responsibility statements that govern your participation." status={asStatus(standingMissing)}>
                <Textarea label="Professional responsibility note" badge={`Required for ${total}/${total}`} value={profile.professionalResponsibilityInstructions} onChange={(value) => update("professionalResponsibilityInstructions", value)} rows={3} placeholder="Note any conflicts policy, bar/regulatory body, or responsibility arrangements relevant to network requests." hint={profile.professionalResponsibilityInstructions.trim() ? undefined : "This is the only item still missing from your member readiness."} />
                <div className="mt-4">
                  <Textarea label="Independence statement" value={profile.independenceStatement} onChange={(value) => update("independenceStatement", value)} rows={4} />
                </div>
              </SectionCard>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ReadinessRing({ value, total }: { value: number; total: number }) {
  const radius = 16;
  const circumference = 2 * Math.PI * radius;
  const ratio = total > 0 ? value / total : 0;
  return (
    <svg viewBox="0 0 40 40" className="h-10 w-10" aria-hidden>
      <circle cx={20} cy={20} r={radius} fill="none" stroke="var(--line)" strokeWidth={4} />
      <circle cx={20} cy={20} r={radius} fill="none" stroke="var(--burg)" strokeWidth={4} strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={circumference * (1 - ratio)} transform="rotate(-90 20 20)" />
      <text x={20} y={21} textAnchor="middle" dominantBaseline="middle" fill="var(--ink)" style={{ fontSize: 11, fontWeight: 700 }}>{value}/{total}</text>
    </svg>
  );
}

function NavRail({ items, active, onJump, showCallout, total, firstMissing }: { items: NavItem[]; active: string; onJump: (id: string) => void; showCallout: boolean; total: number; firstMissing?: string }) {
  return (
    <nav className="lg:sticky lg:top-24 lg:self-start">
      <div className="rounded-2xl border border-line bg-surface p-3 shadow-sm">
        <div className="mono px-2 pb-2 pt-1 text-[10px] uppercase tracking-[0.16em] text-muted">On this page</div>
        <ul className="grid gap-0.5">
          {items.map((item) => (
            <li key={item.id}>
              <button type="button" onClick={() => onJump(item.id)} className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm ${active === item.id ? "bg-primary/10 font-semibold text-primary" : "text-secondary hover:bg-hover"}`}>
                <span className={active === item.id ? "text-primary" : "text-muted"}>{item.icon}</span>
                <span className="truncate">{item.label}</span>
                {item.status !== undefined ? <span className={`ml-auto h-2 w-2 shrink-0 rounded-full ${item.status === "ready" ? "bg-good" : "bg-warn"}`} /> : null}
              </button>
            </li>
          ))}
        </ul>
      </div>
      {showCallout && firstMissing ? (
        <div className="mt-3 flex gap-2 rounded-2xl border border-warn/30 bg-warn/5 p-3 text-xs text-secondary">
          <AlertTriangle className="h-4 w-4 shrink-0 text-warn" />
          <span>Add a <strong className="text-ink">{firstMissing}</strong> to reach {total}/{total}.</span>
        </div>
      ) : null}
    </nav>
  );
}

function SectionCard({ id, icon, title, subtitle, status, children }: { id: string; icon: ReactNode; title: string; subtitle: string; status?: SectionStatus; children: ReactNode }) {
  return (
    <section id={id} className="scroll-mt-28 rounded-2xl border border-line bg-surface p-5 shadow-sm sm:p-6">
      <div className="mb-5 flex items-start gap-3 border-b border-line/70 pb-4">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">{icon}</span>
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-ink">{title}</h2>
          <p className="mt-0.5 text-sm text-secondary">{subtitle}</p>
        </div>
        {status !== undefined ? <div className="ml-auto pl-2"><StatusBadge status={status} /></div> : null}
      </div>
      {children}
    </section>
  );
}

function StatusBadge({ status }: { status: SectionStatus }) {
  if (status === "ready") return <span className="inline-flex items-center gap-1 rounded-full border border-good/30 bg-good/10 px-2.5 py-1 text-[11px] font-semibold text-good"><Check className="h-3 w-3" /> Ready</span>;
  return <span className="inline-flex items-center gap-1 rounded-full border border-warn/30 bg-warn/10 px-2.5 py-1 text-[11px] font-semibold text-warn">{status} missing</span>;
}

function FieldLabel({ children, optional, badge }: { children: ReactNode; optional?: boolean; badge?: string }) {
  return (
    <span className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-ink">
      {children}
      {optional ? <span className="mono text-[10px] font-normal uppercase tracking-[0.14em] text-muted">Optional</span> : null}
      {badge ? <span className="mono text-[10px] font-semibold uppercase tracking-[0.12em] text-primary">{badge}</span> : null}
    </span>
  );
}

const inputClass = "min-h-11 w-full rounded-lg border border-line bg-surface text-sm text-ink placeholder:text-muted/70 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15";

function Field({ label, value, onChange, placeholder, optional, icon, hint }: { label: string; value: string; onChange: (value: string) => void; placeholder?: string; optional?: boolean; icon?: ReactNode; hint?: string }) {
  return (
    <label className="block">
      <FieldLabel optional={optional}>{label}</FieldLabel>
      <div className="relative">
        {icon ? <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted">{icon}</span> : null}
        <input className={`${inputClass} ${icon ? "pl-9 pr-3" : "px-3"}`} value={value} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />
      </div>
      {hint ? <span className="mt-1 block text-xs text-muted">{hint}</span> : null}
    </label>
  );
}

function LockedField({ label, value, badge }: { label: string; value: string; badge?: string }) {
  return (
    <div className="block">
      <FieldLabel>{label}</FieldLabel>
      <div className="flex min-h-11 items-center gap-2 rounded-lg border border-line bg-canvas px-3 text-sm">
        <Lock className="h-3.5 w-3.5 shrink-0 text-muted" />
        <span className="truncate font-medium text-ink">{value}</span>
        {badge ? <span className="ml-auto shrink-0 rounded-md bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">{badge}</span> : null}
      </div>
    </div>
  );
}

function SelectField({ label, value, onChange, options, optional }: { label: string; value: string; onChange: (value: string) => void; options: [string, string][]; optional?: boolean }) {
  return (
    <label className="block">
      <FieldLabel optional={optional}>{label}</FieldLabel>
      <select className={`${inputClass} px-3`} value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map(([optionValue, optionLabel]) => <option key={optionValue} value={optionValue}>{optionLabel}</option>)}
      </select>
    </label>
  );
}

function Textarea({ label, value, onChange, placeholder, hint, badge, rows = 3 }: { label: string; value: string; onChange: (value: string) => void; placeholder?: string; hint?: string; badge?: string; rows?: number }) {
  return (
    <label className="block">
      <FieldLabel badge={badge}>{label}</FieldLabel>
      <textarea rows={rows} className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-muted/70 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15" value={value} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />
      {hint ? <span className="mt-1 block text-xs text-muted">{hint}</span> : null}
    </label>
  );
}

function PhotoControl({ name, imageUrl, shape, uploadLabel, fileName, hint, onSelect, onRemove }: { name: string; imageUrl: string; shape: "circle" | "rounded"; uploadLabel: string; fileName?: string; hint: string; onSelect: (file: File | null) => void; onRemove: () => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="mb-5 flex items-center gap-4">
      <Avatar name={name} imageUrl={imageUrl || undefined} size="lg" shape={shape} />
      <div className="grid gap-2">
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => inputRef.current?.click()} className="inline-flex min-h-9 items-center gap-2 rounded-lg border border-line bg-surface px-3 text-sm font-semibold text-ink hover:bg-hover"><Upload className="h-4 w-4" /> {uploadLabel}</button>
          <button type="button" onClick={onRemove} className="inline-flex min-h-9 items-center rounded-lg px-3 text-sm font-medium text-secondary hover:bg-hover">Remove</button>
        </div>
        <p className="max-w-md text-xs text-muted">{fileName ? `Selected: ${fileName}` : hint}</p>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(event) => onSelect(event.target.files?.[0] ?? null)} />
      </div>
    </div>
  );
}

function EmailPushSubscriptionPicker({ selected, onToggle }: { selected: EmailPushSubject[]; onToggle: (subject: EmailPushSubject) => void }) {
  return (
    <div className="grid gap-2.5 sm:grid-cols-2">
      {emailPushSubjects.map((subject) => {
        const checked = selected.includes(subject.value);
        return (
          <label key={subject.value} className={`flex min-h-12 cursor-pointer items-center gap-3 rounded-xl border px-4 text-sm ${checked ? "border-primary/40 bg-primary/5 text-ink" : "border-line bg-surface text-secondary hover:bg-hover"}`}>
            <input type="checkbox" className="sr-only" checked={checked} onChange={() => onToggle(subject.value)} />
            <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-md border ${checked ? "border-primary bg-primary text-white" : "border-line bg-surface"}`}>{checked ? <Check className="h-3.5 w-3.5" /> : null}</span>
            <span className="font-medium">{subject.label}</span>
          </label>
        );
      })}
    </div>
  );
}

function PracticeAreaPicker({ areas, selected, count, onToggle, onSelectAll, onClear }: { areas: LexData["practiceAreas"]; selected: Set<string>; count: number; onToggle: (areaId: string) => void; onSelectAll: () => void; onClear: () => void }) {
  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm text-secondary"><strong className="text-ink">{count}</strong> selected</span>
        <div className="flex items-center gap-4 text-sm">
          <button type="button" onClick={onSelectAll} className="font-semibold text-primary hover:underline">Select all</button>
          <button type="button" onClick={onClear} className="font-semibold text-secondary hover:underline">Clear</button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {areas.map((area) => {
          const on = selected.has(area.id);
          return (
            <button key={area.id} type="button" onClick={() => onToggle(area.id)} className={`min-h-9 rounded-full border px-3.5 text-sm font-medium ${on ? "border-primary bg-primary/5 text-primary" : "border-line bg-surface text-ink hover:bg-hover"}`}>{area.name}</button>
          );
        })}
      </div>
    </div>
  );
}

function CooperationInterestPicker({ selected, onToggle }: { selected: CooperationRequestType[]; onToggle: (type: CooperationRequestType) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {cooperationRequestTypes.map((item) => {
        const on = selected.includes(item.value);
        return (
          <button key={item.value} type="button" onClick={() => onToggle(item.value)} className={`min-h-9 rounded-full border px-3.5 text-sm font-medium ${on ? "border-primary bg-primary/5 text-primary" : "border-line bg-surface text-ink hover:bg-hover"}`}>{item.label}</button>
        );
      })}
    </div>
  );
}

function FirmCreationCard({ persistence }: { persistence?: ProfilePersistence }) {
  const [name, setName] = useState("");
  const [jurisdiction, setJurisdiction] = useState("");
  const [lawyerCount, setLawyerCount] = useState(1);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [independenceStatement, setIndependenceStatement] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const submit = async () => {
    if (!persistence) {
      setMessage("Firm profiles are created in the live workspace.");
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      await persistence.createMyFirmProfile({ name, jurisdiction, lawyerCount, independenceStatement, websiteUrl: websiteUrl.trim() || null });
      setMessage("Firm profile created. A network member can now propose your firm for admission.");
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "The firm profile could not be created.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mb-5 border-primary/40">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
        <Building2 className="h-4 w-4 text-primary" />
        Create your firm profile
      </div>
      <p className="mb-3 text-sm text-secondary">You're signed in as an observer. Add your firm profile so a network member can propose your firm for membership.</p>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Firm name" value={name} onChange={setName} />
        <label className="block">
          <FieldLabel>Jurisdiction</FieldLabel>
          <JurisdictionSelect value={jurisdiction} onChange={setJurisdiction} />
        </label>
        <Field label="Number of lawyers" value={String(lawyerCount)} onChange={(value) => setLawyerCount(Math.max(1, Math.round(Number(value) || 1)))} />
        <Field label="Website" value={websiteUrl} onChange={setWebsiteUrl} placeholder="https://" optional />
      </div>
      <div className="mt-3">
        <Textarea label="Independence statement" value={independenceStatement} onChange={setIndependenceStatement} placeholder="Briefly confirm your firm's independence and absence of conflicting affiliations." />
      </div>
      <Button type="button" className="mt-3" disabled={busy} onClick={() => void submit()}>Create firm profile</Button>
      {message ? <div className="mt-2 text-[12px] text-secondary">{message}</div> : null}
    </Card>
  );
}

// Normalise the image to a crisp square before upload: photos are centre-cropped (cover),
// logos are fitted on white (contain). Keeps avatars sharp and storage small.
async function prepareSquareImage(file: File, mode: "cover" | "contain", size = 512): Promise<Blob> {
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    const { width: iw, height: ih } = bitmap;
    if (mode === "contain") {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, size, size);
    }
    const scale = mode === "cover" ? Math.max(size / iw, size / ih) : Math.min(size / iw, size / ih);
    const dw = iw * scale;
    const dh = ih * scale;
    ctx.drawImage(bitmap, (size - dw) / 2, (size - dh) / 2, dw, dh);
    bitmap.close?.();
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
    return blob ?? file;
  } catch {
    return file;
  }
}

async function uploadProfileImage(file: File, label: string, persistence: ProfilePersistence) {
  if (!file.type.startsWith("image/")) throw new Error(`Choose an image file for the ${label}.`);
  if (file.size > 8 * 1024 * 1024) throw new Error(`Choose a ${label} smaller than 8 MB.`);
  const prepared = await prepareSquareImage(file, label === "lawyer photo" ? "cover" : "contain");
  const uploadUrl = await persistence.generateProfilePhotoUploadUrl();
  const response = await fetch(uploadUrl, { method: "POST", headers: { "Content-Type": prepared.type || "image/jpeg" }, body: prepared });
  if (!response.ok) throw new Error(`The ${label} upload failed.`);
  return (await response.json()).storageId as string;
}

function splitCommaList(value: string) {
  return [...new Set(value.split(",").map((item) => item.trim()).filter(Boolean))];
}
